// src/hooks/useContextualOnboarding.js
import { useCallback, useEffect, useState } from "react";

function storageKey(pageKey) {
  return `onboarding_seen_${pageKey}`;
}

function safeGetItem(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key, value) {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Hook générique de gestion d'état "vu / non vu" pour un onboarding contextuel,
 * indépendant par page via pageKey. Persistance : localStorage.
 * Comportement sécurisé si localStorage est indisponible (mode privé, quota, etc.) :
 * l'onboarding est alors traité comme "jamais vu" à chaque montage, sans jamais lever d'erreur.
 */
export default function useContextualOnboarding(pageKey) {
  const key = storageKey(pageKey);

  const [seen, setSeen] = useState(() => safeGetItem(key) === "1");

  useEffect(() => {
    setSeen(safeGetItem(key) === "1");
  }, [key]);

  const markSeen = useCallback(() => {
    safeSetItem(key, "1");
    setSeen(true);
  }, [key]);

  const reset = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
    setSeen(false);
  }, [key]);

  return {
    shouldShow: !seen,
    seen,
    markSeen,
    reset,
  };
}
